import { formatMinorCurrency } from 'BoostXpressAdmin/Utils/cashReconciliationMoney'
import React, { useState } from 'react'
import { useStationOperationsExport } from '../hooks/useStationOperationsExport'
import { collectStationOperationsRows } from 'BoostXpressAdmin/Utils/stationOperationsExport'
import { StationLookupOption } from 'BoostXpressAdmin/Services/stationLookupBXService'
import StationOperationsStationFilter from '../components/StationOperationsStationFilter'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import { priceChangeBXService } from 'BoostXpressAdmin/Services/priceChangeBXService'
import { PriceChangeItem, PriceChangeProduct } from 'BoostXpressAdmin/Model/priceChangeXModel'
import { useStationOperationsPage } from '../hooks/useStationOperationsList'
import ListPagination from 'Components/ListPagination/ListPagination'
import { useListPagination } from 'Hooks/useListPagination'
import { stationOperationsError } from 'BoostXpressAdmin/Utils/stationOperations'
import './PriceChange.scss'

const PriceChange: React.FC = () => {
  const { exportCsv: downloadCsv, isExporting, exportError } = useStationOperationsExport()
  const [selectedStation, setSelectedStation] = useState<StationLookupOption | null>(null)
  const [productFilter, setProductFilter] = useState<PriceChangeProduct | 'all'>('all')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const { page, setPage, limit, changePageSize } = useListPagination()

  const filters: Omit<Parameters<typeof priceChangeBXService.list>[0], 'page' | 'limit'> = {
    stationId: selectedStation?.id,
    product: productFilter === 'all' ? undefined : productFilter,
  }

  const {
    items: items,
    total,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useStationOperationsPage<
    PriceChangeItem,
    { stationId?: string; product?: PriceChangeProduct }
  >('priceChange', priceChangeBXService.list, filters, page, limit)
  const exportCsv = () =>
    downloadCsv(
      () => collectStationOperationsRows('price_changes', priceChangeBXService.list, filters),
      (item) => ({
        Station: item.station,
        Product: item.product,
        Pump: item.pump,
        'Totalizer Reading': item.totalizerReading.toLocaleString(),
        'New Price': formatMinorCurrency(item.newPriceMinor),
        'Report Date': item.effectiveFrom,
      }),
      'price_changes',
    )
  const formatPrice = formatMinorCurrency

  return (
    <div className='bx-price-change-page'>
      {exportError && (
        <div className='alert alert-danger' role='alert'>
          {exportError}
        </div>
      )}
      {isError && (
        <div className='alert alert-danger' role='alert'>
          {stationOperationsError(error)}{' '}
          <button type='button' onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}
      {/* Header section matching Figma & Reconciliation */}
      <div className='bx-price-change-page__header'>
        <h2 className='bx-price-change-page__title'>Price Changes</h2>
        <p className='bx-price-change-page__subtitle'>
          Every price change is pinned to a totalizer reading, so engineers can tell exactly which
          sale used the old price and which used the new one.
        </p>
      </div>

      {/* Search & Actions Toolbar matching Reconciliation */}
      <div className='bx-price-change-page__controls'>
        <StationOperationsStationFilter
          value={selectedStation}
          onChange={(station) => {
            setSelectedStation(station)
            setPage(1)
          }}
        />

        <div className='bx-price-change-page__actions'>
          <div className='bx-filter-dropdown-wrapper'>
            <button
              type='button'
              className={`bx-btn-outline ${productFilter !== 'all' ? 'active' : ''}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <img src={filterIcon} alt='' />
              <span>Filters</span>
            </button>

            {isFilterOpen && (
              <div className='bx-filter-menu'>
                <div className='bx-filter-menu__title'>Product</div>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('all')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  All Products
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'PMS' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('PMS')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  PMS
                </button>
                <button
                  type='button'
                  className={`bx-filter-menu__item ${productFilter === 'AGO' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('AGO')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  AGO
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className='bx-btn-outline'
            onClick={exportCsv}
            disabled={isFetching || isExporting || isError || !total}
          >
            <img src={exportIcon} alt='' />
            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Main Table matching Figma & Reconciliation */}
      <div className='bx-price-change-page__table-wrapper'>
        <table className='bx-price-change-table'>
          <thead>
            <tr>
              <th>Station</th>
              <th>Product</th>
              <th>Pump</th>
              <th>Totalizer reading</th>
              <th>New price</th>
              <th>Report date</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className='bx-pc-empty'>
                  Loading price changes...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className='bx-pc-empty'>
                  No price changes found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className='bx-pc-col-station'>{item.station}</td>
                  <td className='bx-pc-col-product'>{item.product}</td>
                  <td className='bx-pc-col-pump'>{item.pump}</td>
                  <td className='bx-pc-col-reading'>{item.totalizerReading.toLocaleString()}</td>
                  <td className='bx-pc-col-price'>{formatPrice(item.newPriceMinor)}</td>
                  <td className='bx-pc-col-effective'>{item.effectiveFrom}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ListPagination
        page={page}
        limit={limit}
        total={total}
        disabled={isFetching}
        onPageChange={setPage}
        onPageSizeChange={changePageSize}
      />
    </div>
  )
}

export default PriceChange
